import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar, DollarSign, Brain, Sparkles, Check, Plus } from 'lucide-react';
import { Booking, Expense, Insight, ServiceType, ExpenseCategory, LinkedType } from '../types';
import { getBusinessSettings } from '../utils/businessSettings';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

// 1. Redesigned Add Booking Modal
interface AddBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  streams: ServiceType[];
}

// 1. Redesigned Add Booking Modal
export function AddBookingModal({ isOpen, onClose, onSubmit, streams }: AddBookingModalProps) {
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientCompany, setClientCompany] = useState('');
  const [type, setType] = useState<ServiceType>(streams[0] || 'consult');
  const [isCustomType, setIsCustomType] = useState(false);
  const [customTypeInput, setCustomTypeInput] = useState('');
  const [price, setPrice] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState('');

  // J-Lens Integration (Bonus)
  const [showInsight, setShowInsight] = useState(false);
  const [insightTitle, setInsightTitle] = useState('');
  const [insightNote, setInsightNote] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setClientName('');
      setClientEmail('');
      setClientCompany('');
      setType(streams[0] || 'consult');
      setIsCustomType(false);
      setCustomTypeInput('');
      setPrice('');
      setDate(new Date().toISOString().split('T')[0]);
      setDueDate('');
      setError('');
      setShowInsight(false);
      setInsightTitle('');
      setInsightNote('');
      
      // Auto-focus after modal transition completes
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }
  }, [isOpen, streams]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Low validation constraint: price and type are required, name is optional
    const parsedPrice = parseFloat(price);
    if (isNaN(parsedPrice) || parsedPrice < 0) {
      setError('Please enter a valid price (>= 0)');
      return;
    }

    const resolvedStream = isCustomType && customTypeInput.trim() ? customTypeInput.trim() : type;
    const finalClientName = clientName.trim() || (resolvedStream.toLowerCase().includes('salary') ? 'Director / Self Draw' : 'Anonymous Client');
    const finalDate = date ? new Date(date).toISOString() : new Date().toISOString();

    onSubmit({
      client_name: finalClientName,
      client_email: clientEmail.trim() || undefined,
      client_company: clientCompany.trim() || undefined,
      type: resolvedStream,
      price: parsedPrice,
      date: finalDate,
      due_date: dueDate ? new Date(dueDate).toISOString() : undefined,
      // Pass optional inline insight
      insight_title: showInsight ? (insightTitle.trim() || `Reflection on booking for ${finalClientName}`) : undefined,
      insight_note: showInsight ? insightNote.trim() : undefined,
    });
    
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2.5 sm:p-4 overflow-y-auto bg-[#0C0E0F]/80 backdrop-blur-md">
      {/* Backdrop clickable area */}
      <div className="absolute inset-0 cursor-pointer" onClick={onClose} />
      
      {/* Content - Fully scrollable on mobile */}
      <div className="relative my-auto w-full max-w-md max-h-[88vh] overflow-y-auto rounded-2xl bg-[#151819] border border-[#2A2F31] p-4 sm:p-6 shadow-2xl transition-all duration-300 scrollbar-thin scrollbar-thumb-gray-800">
        
        {/* Header - No clutter */}
        <div className="flex items-center justify-between border-b border-[#1F2426] pb-4 mb-5">
          <h3 className="text-lg font-display font-semibold text-white flex items-center gap-2">
            <span className="p-1 bg-teal-accent/10 rounded-lg text-teal-accent text-sm">📅</span> New Booking
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-[#2A2F31]/30 transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-xs bg-rose-500/10 text-rose-400 rounded-lg border border-rose-500/20 font-medium">
              {error}
            </div>
          )}

          {/* Client Name & Optional Company */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Client Name</label>
              <input
                ref={inputRef}
                type="text"
                placeholder="Optional (e.g. Sarah)"
                className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2.5 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-xs font-medium"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Client Company (Optional)</label>
              <input
                type="text"
                placeholder="e.g. Horizon Media / Partner Studio"
                className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2.5 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-xs font-medium"
                value={clientCompany}
                onChange={(e) => setClientCompany(e.target.value)}
              />
            </div>
          </div>

          {/* Client Email */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Client Email (Optional)</label>
            <input
              type="email"
              placeholder="e.g. client@example.com"
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-2.5 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-xs font-medium"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
            />
          </div>

          {/* Service Stream Select & Quick Presets */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm text-gray-400">Income Stream</label>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    setType('salary');
                    setIsCustomType(false);
                  }}
                  className={`text-[11px] px-2 py-0.5 rounded-md border transition-all ${
                    type === 'salary' && !isCustomType
                      ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 font-semibold'
                      : 'bg-[#151819] text-gray-400 border-[#2A2F31] hover:border-emerald-500/40 hover:text-emerald-300'
                  }`}
                >
                  💼 Founder Salary / Draw
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex gap-2">
                <select
                  className="flex-1 bg-[#151819] border border-[#2A2F31] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-sm font-medium capitalize"
                  value={isCustomType ? '__other__' : type}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '__other__') {
                      setIsCustomType(true);
                    } else {
                      setIsCustomType(false);
                      setType(val as ServiceType);
                    }
                  }}
                >
                  {/* Default streams from DB */}
                  {streams.map((str) => (
                    <option key={str} value={str} className="bg-[#151819]">
                      {str}
                    </option>
                  ))}
                  {!streams.includes('salary') && (
                    <option value="salary" className="bg-[#151819]">
                      Salary / Founder Draw
                    </option>
                  )}
                  <option value="__other__" className="bg-[#151819] text-gold-accent font-semibold">
                    + Other (Specify custom income...)
                  </option>
                </select>
              </div>

              {/* Conditional custom stream specification if Other is selected */}
              {isCustomType && (
                <div className="bg-[#0C0E0F] border border-gold-accent/30 rounded-xl p-3 space-y-2 animate-fade-in">
                  <div className="flex items-center justify-between text-xs text-gold-accent font-semibold">
                    <span>Specify Other Income Stream</span>
                    <span className="text-[10px] text-gray-500">e.g. Dividend, Royalty, Retainer</span>
                  </div>
                  <input
                    type="text"
                    required={isCustomType}
                    placeholder="Enter custom stream name..."
                    className="w-full bg-[#151819] border border-[#2A2F31] rounded-lg px-3 py-2 text-white placeholder:text-gray-500 focus:outline-none focus:border-gold-accent text-xs font-medium"
                    value={customTypeInput}
                    onChange={(e) => setCustomTypeInput(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>


          {/* Price - Required - Premium Prefix inside */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Price</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm select-none">{getBusinessSettings().currencyCode || 'ZAR'}</span>
              <input
                type="number"
                step="0.01"
                required
                placeholder="0.00"
                className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl pl-14 pr-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-sm font-semibold"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>
          </div>

          {/* Booking Date & Optional Due Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Booking Date</label>
              <input
                type="date"
                className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-sm font-medium"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Due Date <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="date"
                className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-sm font-medium"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          {/* J-Lens Toggle Button (Bonus) */}
          <div className="pt-2">
            {!showInsight ? (
              <button
                type="button"
                onClick={() => setShowInsight(true)}
                className="text-xs text-gold-accent hover:text-white font-medium flex items-center gap-1.5 transition-colors focus:outline-none"
              >
                <Plus size={14} className="stroke-[2.5]" /> + Add J-Lens Insight
              </button>
            ) : (
              <div className="space-y-4 pt-4 border-t border-[#1F2426] animate-fade-in">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gold-accent uppercase tracking-wider flex items-center gap-1">
                    🧠 J-Lens Reflection
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowInsight(false)}
                    className="text-[10px] text-gray-500 hover:text-gray-300"
                  >
                    Remove
                  </button>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Reflection Title</label>
                  <input
                    type="text"
                    placeholder="e.g. Booking conversion pattern"
                    className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-xs font-medium"
                    value={insightTitle}
                    onChange={(e) => setInsightTitle(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-400 mb-1">Observation / Rule</label>
                  <textarea
                    rows={2}
                    placeholder="Capture the learning instantly while fresh..."
                    className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-xs font-medium resize-none leading-relaxed"
                    value={insightNote}
                    onChange={(e) => setInsightNote(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <button
            type="submit"
            className="w-full h-12 mt-6 bg-[#14B8A6] text-black rounded-xl font-medium hover:bg-[#0EA5A4] transition active:scale-95 flex items-center justify-center gap-2 text-sm shadow-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
          >
            Create Booking
          </button>
        </form>
      </div>
    </div>
  );
}

// 2. Redesigned Add Expense Modal
interface AddExpenseModalProps extends ModalProps {
  streams: ServiceType[];
}

export function AddExpenseModal({ isOpen, onClose, onSubmit, streams }: AddExpenseModalProps) {
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState<ExpenseCategory>('travel');
  const [linkedStream, setLinkedStream] = useState<ServiceType>(streams[0] || '');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState<'monthly' | 'yearly'>('monthly');
  const [error, setError] = useState('');

  // J-Lens Integration (Bonus)
  const [showInsight, setShowInsight] = useState(false);
  const [insightTitle, setInsightTitle] = useState('');
  const [insightNote, setInsightNote] = useState('');

  const amountInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setCategory('travel');
      setLinkedStream(streams[0] || '');
      setNote('');
      setDate(new Date().toISOString().split('T')[0]);
      setIsRecurring(false);
      setFrequency('monthly');
      setError('');
      setShowInsight(false);
      setInsightTitle('');
      setInsightNote('');


      // Auto-focus Amount immediately
      setTimeout(() => {
        if (amountInputRef.current) {
          amountInputRef.current.focus();
        }
      }, 50);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Amount is required validation
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Please enter a valid expense amount (> 0)');
      return;
    }

    const finalNote = note.trim() || `${category.charAt(0).toUpperCase() + category.slice(1)} Operational Expense`;
    const finalDate = date ? new Date(date).toISOString() : new Date().toISOString();

    onSubmit({
      amount: parsedAmount,
      category,
      linked_stream: linkedStream,
      note: finalNote,
      date: finalDate,
      is_recurring: isRecurring,
      frequency: isRecurring ? frequency : undefined,
      // Pass optional inline insight
      insight_title: showInsight ? (insightTitle.trim() || `Reflection on cost: ${finalNote}`) : undefined,
      insight_note: showInsight ? insightNote.trim() : undefined,
    });
    
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2.5 sm:p-4 overflow-y-auto bg-[#0C0E0F]/80 backdrop-blur-md">
      <div className="absolute inset-0 cursor-pointer" onClick={onClose} />
      
      <div className="relative my-auto w-full max-w-md max-h-[88vh] overflow-y-auto rounded-2xl bg-[#151819] border border-[#2A2F31] p-4 sm:p-6 shadow-2xl transition-all duration-300 scrollbar-thin scrollbar-thumb-gray-800">
        
        {/* Header - No clutter */}
        <div className="flex items-center justify-between border-b border-[#1F2426] pb-4 mb-5">
          <h3 className="text-lg font-display font-semibold text-white flex items-center gap-2">
            <span className="p-1 bg-rose-500/10 rounded-lg text-rose-400 text-sm">💸</span> Record Cost
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-[#2A2F31]/30 transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="p-3 text-xs bg-rose-500/10 text-rose-400 rounded-lg border border-rose-500/20 font-medium">
              {error}
            </div>
          )}

          {/* Amount - Required - Auto focused */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Amount</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm select-none">{getBusinessSettings().currencyCode || 'ZAR'}</span>
              <input
                ref={amountInputRef}
                type="number"
                step="0.01"
                required
                placeholder="0.00"
                className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl pl-14 pr-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-sm font-semibold"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          </div>

          {/* Category - Pill design */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm text-gray-400">Expense Category</label>
              {category === 'salary' && (
                <span className="text-[11px] text-emerald-400 font-medium">Team & Staff Payroll / Founder Draw</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {(['salary', 'travel', 'supplies', 'rent', 'other'] as ExpenseCategory[]).map((cat) => {
                const isSelected = category === cat;
                const labels: Record<ExpenseCategory, string> = {
                  salary: '💼 Salary / Payroll',
                  travel: '✈️ Travel',
                  supplies: '📦 Supplies',
                  rent: '🏢 Rent',
                  other: '✨ Other'
                };
                return (
                  <button
                    key={cat}
                    type="button"
                    className={`px-3 py-1.5 rounded-full border text-sm transition-all duration-200 active:scale-95 capitalize flex items-center gap-1.5 ${
                      isSelected
                        ? cat === 'salary'
                          ? 'bg-emerald-400 text-black border-emerald-400 font-semibold'
                          : 'bg-[#14B8A6] text-black border-[#14B8A6] font-semibold'
                        : 'bg-[#151819] text-gray-300 border-[#2A2F31] hover:border-[#14B8A6]'
                    }`}
                    onClick={() => {
                      setCategory(cat);
                      if (cat === 'salary' || cat === 'rent') {
                        setIsRecurring(true);
                      }
                    }}
                  >
                    {labels[cat]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* If "other" category is selected, provide dedicated cost specification */}
          {category === 'other' && (
            <div className="bg-[#0C0E0F] border border-amber-500/40 rounded-xl p-3.5 space-y-2 animate-fade-in">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-semibold text-amber-400">
                  Stipulate Other Cost / Purpose <span className="text-amber-500">*</span>
                </label>
                <span className="text-[10px] text-gray-500">e.g. Legal, SaaS, Insurance</span>
              </div>
              <input
                type="text"
                required={category === 'other'}
                placeholder="Specify what this expense was for..."
                className="w-full bg-[#151819] border border-[#2A2F31] rounded-lg px-3 py-2.5 text-white placeholder:text-gray-500 focus:outline-none focus:border-amber-400 text-xs font-medium"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          )}

          {/* Linked Service Stream - Pill design */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Linked Service Stream</label>
            <div className="flex flex-wrap gap-2">
              {streams.map((str) => (
                <button
                  key={str}
                  type="button"
                  className={`px-3 py-1.5 rounded-full border text-sm transition-all duration-200 active:scale-95 capitalize ${
                    linkedStream === str
                      ? 'bg-[#14B8A6] text-black border-[#14B8A6] font-semibold'
                      : 'bg-[#151819] text-gray-300 border-[#2A2F31] hover:border-[#14B8A6]'
                  }`}
                  onClick={() => setLinkedStream(str)}
                >
                  {str}
                </button>
              ))}
            </div>
          </div>

          {/* Note / Description - For non-other categories */}
          {category !== 'other' && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                {category === 'salary' ? 'Employee / Role / Notes' : 'Note / Description (Optional)'}
              </label>
              <input
                type="text"
                placeholder={
                  category === 'salary' 
                    ? "e.g. Senior Developer monthly payroll, Founder monthly draw" 
                    : "Optional (e.g. Airport rides, Adobe Suite)"
                }
                className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-sm font-medium"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          )}

          {/* Date Incurred */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Date Incurred</label>
            <input
              type="date"
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-sm font-medium"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          {/* Recurring Option */}
          <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-semibold text-white">Recurring Overhead</label>
                <p className="text-[11px] text-gray-500">Enable for repeating subscriptions or rent</p>
              </div>
              <button
                type="button"
                onClick={() => setIsRecurring(!isRecurring)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  isRecurring ? 'bg-[#14B8A6]' : 'bg-[#2A2F31]'
                }`}
                role="switch"
                aria-checked={isRecurring}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    isRecurring ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {isRecurring && (
              <div className="pt-3 border-t border-[#1F2426] space-y-2.5 animate-fade-in">
                <label className="block text-xs text-gray-400 font-medium">Billing Frequency</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['monthly', 'yearly'] as const).map((freq) => (
                    <button
                      key={freq}
                      type="button"
                      onClick={() => setFrequency(freq)}
                      className={`py-2 rounded-xl border text-xs font-semibold capitalize transition-all duration-200 active:scale-95 ${
                        frequency === freq
                          ? 'bg-[#14B8A6]/10 text-[#14B8A6] border-[#14B8A6]'
                          : 'bg-[#151819] text-gray-400 border-[#2A2F31] hover:border-[#14B8A6]'
                      }`}
                    >
                      {freq}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* J-Lens Toggle Button (Bonus) */}
          <div className="pt-2">
            {!showInsight ? (
              <button
                type="button"
                onClick={() => setShowInsight(true)}
                className="text-xs text-gold-accent hover:text-white font-medium flex items-center gap-1.5 transition-colors focus:outline-none"
              >
                <Plus size={14} className="stroke-[2.5]" /> + Add J-Lens Insight
              </button>
            ) : (
              <div className="space-y-4 pt-4 border-t border-[#1F2426] animate-fade-in">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gold-accent uppercase tracking-wider flex items-center gap-1">
                    🧠 J-Lens Reflection
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowInsight(false)}
                    className="text-[10px] text-gray-500 hover:text-gray-300"
                  >
                    Remove
                  </button>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Reflection Title</label>
                  <input
                    type="text"
                    placeholder="e.g. High customer acquisitions cost"
                    className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-xs font-medium"
                    value={insightTitle}
                    onChange={(e) => setInsightTitle(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-400 mb-1">Observation / Rule</label>
                  <textarea
                    rows={2}
                    placeholder="Capture the learning instantly while fresh..."
                    className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-xs font-medium resize-none leading-relaxed"
                    value={insightNote}
                    onChange={(e) => setInsightNote(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <button
            type="submit"
            className="w-full h-12 mt-6 bg-[#14B8A6] text-black rounded-xl font-medium hover:bg-[#0EA5A4] transition active:scale-95 flex items-center justify-center gap-2 text-sm shadow-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
          >
            Create Expense
          </button>
        </form>
      </div>
    </div>
  );
}

// 3. Redesigned Add Insight Modal (J-Lens)
interface AddInsightModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  bookings: Booking[];
  expenses: Expense[];
  prefilledType?: LinkedType;
  prefilledId?: string;
}

export function AddInsightModal({
  isOpen,
  onClose,
  onSubmit,
  bookings,
  expenses,
  prefilledType = 'general',
  prefilledId = ''
}: AddInsightModalProps) {
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [linkedType, setLinkedType] = useState<LinkedType>('general');
  const [linkedId, setLinkedId] = useState('');
  const [error, setError] = useState('');

  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setNote('');
      setLinkedType(prefilledType);
      setLinkedId(prefilledId);
      setError('');

      // Auto-focus Title
      setTimeout(() => {
        if (titleInputRef.current) {
          titleInputRef.current.focus();
        }
      }, 50);
    }
  }, [isOpen, prefilledType, prefilledId]);

  // Adjust linked ID automatically if type changes
  useEffect(() => {
    if (!prefilledId && isOpen) {
      setLinkedId('');
    }
  }, [linkedType]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Please provide an elegant title');
      return;
    }
    if (!note.trim()) {
      setError('Please write down your insight');
      return;
    }
    onSubmit({
      title: title.trim(),
      note: note.trim(),
      linked_type: linkedType,
      linked_id: linkedType === 'general' ? '' : linkedId,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2.5 sm:p-4 overflow-y-auto bg-[#0C0E0F]/80 backdrop-blur-md">
      <div className="absolute inset-0 cursor-pointer" onClick={onClose} />
      
      <div className="relative my-auto w-full max-w-md max-h-[88vh] overflow-y-auto rounded-2xl bg-[#151819] border border-[#2A2F31] p-4 sm:p-6 shadow-2xl transition-all duration-300 scrollbar-thin scrollbar-thumb-gray-800">
        
        {/* Header - No clutter */}
        <div className="flex items-center justify-between border-b border-[#1F2426] pb-4 mb-5">
          <h3 className="text-lg font-display font-semibold text-white flex items-center gap-2">
            <span className="p-1 bg-gold-accent/10 rounded-lg text-gold-accent text-sm">🧠</span> Capture J-Lens Note
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-[#2A2F31]/30 transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="p-3 text-xs bg-rose-500/10 text-rose-400 rounded-lg border border-rose-500/20 font-medium">
              {error}
            </div>
          )}

          {/* Title - Auto focused */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Insight Title</label>
            <input
              ref={titleInputRef}
              type="text"
              required
              placeholder="e.g. Conversion Trigger on Retreats"
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-sm font-medium"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Context Reference */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Context Reference</label>
            <div className="flex flex-wrap gap-2">
              {(['general', 'booking', 'expense'] as LinkedType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  disabled={!!prefilledId} // Lock selection if prefilled
                  className={`px-3 py-1.5 rounded-full border text-sm transition-all duration-200 active:scale-95 capitalize ${
                    linkedType === type
                      ? 'bg-gold-accent text-black border-gold-accent font-semibold disabled:opacity-100'
                      : 'bg-[#151819] text-gray-300 border-[#2A2F31] hover:border-[#14B8A6] disabled:opacity-40'
                  }`}
                  onClick={() => setLinkedType(type)}
                >
                  {type}
                </button>
              ))}
            </div>
            {prefilledId && (
              <p className="text-[11px] text-gold-accent font-medium mt-1.5">
                ✓ Locked to selected item context
              </p>
            )}
          </div>

          {linkedType !== 'general' && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Choose {linkedType === 'booking' ? 'Booking' : 'Expense'}
              </label>
              <select
                disabled={!!prefilledId}
                className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-sm font-medium capitalize"
                value={linkedId}
                onChange={(e) => setLinkedId(e.target.value)}
                required
              >
                <option value="" className="bg-[#151819] text-gray-400">-- Choose Context Item --</option>
                {linkedType === 'booking'
                  ? bookings.map((b) => (
                      <option key={b.id} value={b.id} className="bg-[#151819] text-white">
                        {b.client_name} - {b.type} (${b.price})
                      </option>
                    ))
                  : expenses.map((e) => (
                      <option key={e.id} value={e.id} className="bg-[#151819] text-white">
                        {e.note.substring(0, 24)}... (${e.amount})
                      </option>
                    ))}
              </select>
            </div>
          )}

          {/* Insight / Decision Note */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Insight / Decision Note</label>
            <textarea
              required
              rows={4}
              placeholder="What did you learn? What decisions will you make based on this context?"
              className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl p-4 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-sm font-medium resize-none leading-relaxed"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <button
            type="submit"
            className="w-full h-12 mt-6 bg-[#14B8A6] text-black rounded-xl font-medium hover:bg-[#0EA5A4] transition active:scale-95 flex items-center justify-center gap-2 text-sm shadow-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
          >
            <Sparkles size={16} />
            Capture to J-Lens
          </button>
        </form>
      </div>
    </div>
  );
}
